// What the scan says about one stage of the game from the reader's side:
// where the stage runs, the score when it began and at the reader's first
// move after it, the swing between them, and the reader's blunders and
// misses inside it with the engine's move beside each. Pure functions
// over the review state's phases and the stored scan; honest about a scan
// that is still running.
import { type Color, type Fen, type MistakeRules, type Uci, type Judgement, type MoveJudgement, type Scan, type Phase, type ReviewPhases } from '@chess/api-model';
import { judgeMove, judgementFrom, positionAt } from '@chess/api-analysis';
import { isBlunderUnder, isMissOf, moveKindOf, moverOf, Score, openingExit, phaseRange, readerPawns, readerScoreText, scoreForReader, sideToMove, type ReaderScore, type ReviewGame } from '@chess/api-review';

// The reader-side score rules live in the review API, where the opening
// history reads them too, so the two show the same numbers. They are
// re-exported here under the names this plugin's tests and view use.
export { moverOf, sideToMove, MATE_PAWNS } from '@chess/api-review';
export type { ReaderScore } from '@chess/api-review';
export { scoreForReader as forReader, readerPawns as pawns, readerMoveAfter as endPlyOf, readerScoreText as barScoreText } from '@chess/api-review';

export const STAGE_NAME: Record<Phase, string> = { opening: 'Opening', middlegame: 'Middlegame', ending: 'Ending' };

export interface StageMove {
  /** 1-based: the move is `moves[ply - 1]`. */
  ply: number;
  san: string;
  judgement: Judgement;
  /** Centipawns given up. */
  delta: number;
  /** The engine's move from the position before, in UCI and, when the rules were given, in SAN. */
  best: Uci;
  bestSan?: string;
}

export interface StageHead {
  phase: Phase;
  /** The stage's first and last positions; its moves are `fromPly + 1 … toPly`. */
  fromPly: number;
  toPly: number;
  /** The game ended before this stage began. */
  ended: boolean;
  /** Where the end score is read: the position after the reader's first move following the stage, or the stage's last position. */
  endPly: number;
  /** Scores from the reader's side; undefined until the scan has the position. The start of the game is always level. */
  start?: ReaderScore;
  end?: ReaderScore;
  /** `end − start` in pawns; positive is good for the reader. */
  swing?: number;
  blunders: StageMove[];
  /** Moves where the reader had a clearly winning line and played something else, blunders excepted. */
  misses: StageMove[];
  /** How many of the reader's moves in the stage the scan has judged, and how many there are. */
  judged: number;
  readerMoves: number;
  /** Every position from the stage's start to `endPly` is in the scan. */
  complete: boolean;
}

/** "1 … 9": the move numbers a stage spans. */
export function rangeText(fromPly: number, toPly: number): string {
  if (toPly <= fromPly) return 'none';
  const first = Math.floor(fromPly / 2) + 1;
  const last = Math.ceil(toPly / 2);
  return first === last ? `${last}` : `${first} … ${last}`;
}

/** "12. Nf3" or "12... Nf6" for the move at a 1-based ply. */
export function plyText(game: Pick<ReviewGame, 'moves'>, ply: number): string {
  const move = game.moves[ply - 1];
  if (!move) return '';
  return `${Math.ceil(ply / 2)}${ply % 2 === 1 ? '.' : '...'} ${move.san}`;
}

// The miss rule is the review API's (`GameScan.isMiss`), the one the puzzle
// finder's missed opportunity mirrors; re-exported under this plugin's name.
export { isMissOf as isMiss, MISS_GAP_CP } from '@chess/api-review';

/** The stage's head from the review state's phases and the scan. `toSan` turns the engine's move into SAN when the rules are to hand; `rules` are the user's blunder and miss rules (the defaults when left out). */
export function stageHead(
  scan: Scan | undefined,
  game: Pick<ReviewGame, 'moves' | 'positions' | 'phases'>,
  phase: Phase,
  reader: Color,
  toSan?: (fen: Fen, uci: Uci) => string | undefined,
  rules?: MistakeRules,
): StageHead {
  const moveCount = game.moves.length;
  // The stage's moves are an exclusive span ending on the reader's move where
  // it is scored (the review API's `Stages`), so the next stage starts on the
  // move after this one ends. The opening's end is read through `openingExit`,
  // which the opening history uses as well, from the same span.
  const { fromPly, toPly } = phaseRange(game.phases, moveCount, phase, reader);
  const ended = toPly <= fromPly && (phase !== 'opening' || moveCount === 0);
  const exit = phase === 'opening' ? openingExit(scan, game.phases, moveCount, reader) : undefined;
  const endPly = toPly;
  const head: StageHead = { phase, fromPly, toPly, ended, endPly, blunders: [], misses: [], judged: 0, readerMoves: 0, complete: false };
  if (ended) return head;

  const first = positionAt(scan, fromPly);
  const last = positionAt(scan, endPly);
  // The game starts level whatever the engine thinks of the first move, so a
  // stage that begins at move 1 starts from 0.00 and its delta is its end.
  if (fromPly === 0) head.start = { cp: 0 };
  else if (first) head.start = scoreForReader(first.score, sideToMove(first.fen), reader);
  if (exit) head.end = exit.score;
  else if (last) head.end = scoreForReader(last.score, sideToMove(last.fen), reader);
  if (head.start && head.end) head.swing = readerPawns(head.end) - readerPawns(head.start);

  let covered = !!first;
  for (let ply = fromPly + 1; ply <= toPly; ply++) {
    if (!positionAt(scan, ply)) covered = false;
    if (moverOf(ply) !== reader) continue;
    head.readerMoves++;
    const move = game.moves[ply - 1];
    const stored: MoveJudgement | undefined = judgementFrom(scan, ply - 1);
    const before = positionAt(scan, ply - 1);
    const after = positionAt(scan, ply);
    const judgement = stored ?? (before && after && move ? judgeMove(before, after, move, ply) : undefined);
    if (!judgement) continue;
    head.judged++;
    const kind = moveKindOf(judgement, before?.lines, move?.uci, after ? Score.at(after, reader) : undefined, rules);
    if (!kind) continue;
    const blunder = kind === 'blunder';
    const fenBefore = game.positions[ply - 1];
    const entry: StageMove = {
      ply,
      san: move.san,
      judgement: judgement.judgement,
      delta: judgement.delta,
      best: judgement.best,
      bestSan: fenBefore && toSan ? toSan(fenBefore, judgement.best) : undefined,
    };
    (blunder ? head.blunders : head.misses).push(entry);
  }
  head.complete = covered;
  return head;
}

/** Positive is good for the reader, negative bad, undefined when a score is missing. */
export function deltaTone(head: Pick<StageHead, 'start' | 'end' | 'swing'>): 'good' | 'bad' | 'level' | undefined {
  if (!head.start || !head.end) return undefined;
  const swing = head.swing ?? 0;
  return swing > 0.005 ? 'good' : swing < -0.005 ? 'bad' : 'level';
}

/**
 * The top-right figure: the change between the stage's first and last scores
 * from the reader's side, in the score bar's notation. A stage that starts
 * at move 1 starts level, so its figure is its end score, formatted from the
 * same value by the same function as the "to" chip, and the two cannot
 * disagree. A mate at either end says what happened instead of a number.
 */
export function deltaText(head: Pick<StageHead, 'fromPly' | 'start' | 'end' | 'swing'>): string | undefined {
  if (!head.start || !head.end) return undefined;
  if (head.fromPly === 0) return readerScoreText(head.end);
  const swing = head.swing ?? 0;
  const mateInvolved = head.start.mate !== undefined || head.end.mate !== undefined;
  if (mateInvolved) return swing > 0 ? 'to a mate' : swing < 0 ? 'to being mated' : '+0.00';
  return readerScoreText({ cp: Math.round(swing * 100) });
}

/** The move number of the position after `ply`; the start position counts as move 1. */
export function moveNumberOf(ply: number): number {
  return Math.max(1, Math.ceil(ply / 2));
}

/**
 * A ply's move number with whose move it is: "2." for White's, "2…" for
 * Black's. Two stages that meet mid-move ("to 2." and "from 2…") then read
 * as the distinct, consecutive moves they are.
 */
export function moveLabelOf(ply: number): string {
  const p = Math.max(1, ply);
  return `${moveNumberOf(p)}${p % 2 === 1 ? '.' : '…'}`;
}

/** The opening's mark: a star for theory left with the best move, a blunder mark for theory left with a blunder. */
export type OpeningMark = 'star' | 'blunder';

/**
 * The opening's mark, from the review's phases (who left the book, where the
 * book ended) and the scan's judgements.
 *
 * - `star`: the reader played the theory, never leaving the book first, and
 *   their first move after the book was the engine's best.
 * - `blunder`: the theory was left with a blunder of the reader's: the move
 *   that left the book, when the reader left it, or the reader's first move
 *   after the opponent left it.
 *
 * Undefined otherwise: no reader side, no book reading, the game never left
 * the book, nothing follows it, or the deciding move is unjudged.
 */
export function openingMark(
  game: Pick<ReviewGame, 'moves'> & { phases: Pick<ReviewPhases, 'lastKnownPly' | 'leftBy'> },
  scan: Scan | undefined,
  reader: Color | undefined,
  rules?: MistakeRules,
): OpeningMark | undefined {
  const { lastKnownPly, leftBy } = game.phases;
  if (!reader || lastKnownPly === undefined || leftBy === undefined) return undefined;
  if (leftBy === reader) {
    // The reader's own move left the book: only a blunder there earns a mark.
    if (lastKnownPly >= game.moves.length) return undefined;
    const own = judgementFrom(scan, lastKnownPly);
    return own && isBlunderUnder(own, rules) ? 'blunder' : undefined;
  }
  const exit = lastKnownPly + 1; // the opponent left from the last known position; the reader replies here
  if (exit >= game.moves.length) return undefined;
  const judgement = judgementFrom(scan, exit);
  if (judgement?.judgement === 'best') return 'star';
  if (judgement && isBlunderUnder(judgement, rules)) return 'blunder';
  return undefined;
}

/** Whether the opening earns its star; kept for callers that only need the star. */
export function openingStar(
  game: Pick<ReviewGame, 'moves'> & { phases: Pick<ReviewPhases, 'lastKnownPly' | 'leftBy'> },
  scan: Scan | undefined,
  reader: Color | undefined,
): boolean | undefined {
  const { lastKnownPly, leftBy } = game.phases;
  if (!reader || lastKnownPly === undefined || leftBy === undefined) return undefined;
  if (leftBy === reader) return false;
  const exit = lastKnownPly + 1;
  if (exit >= game.moves.length) return undefined;
  const judgement = judgementFrom(scan, exit);
  if (!judgement) return undefined;
  return judgement.judgement === 'best';
}
